import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  MessageCircle,
  Heart,
  Target,
  CreditCard,
  Gift,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Users,
  Bookmark,
  Route,
  Calendar,
  BookOpen,
  BarChart3,
  Share2,
  CalendarDays,
  Crown,
  Clock,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n/I18nProvider";
import { Tip } from "@/components/Tip";
import { HelpCenter } from "@/components/HelpCenter";

const BASE_NAV = [
  { path: "/chat", icon: MessageCircle, tKey: "nav.chat" },
  { path: "/bookmarks", icon: Bookmark, tKey: "nav.bookmarks" },
  { path: "/mood", icon: Heart, tKey: "nav.mood" },
  { path: "/calendar", icon: CalendarDays, tKey: "nav.calendar" },
  { path: "/therapy", icon: Route, tKey: "nav.therapy" },
  { path: "/timeline", icon: Calendar, tKey: "nav.timeline" },
  { path: "/goals", icon: Target, tKey: "nav.goals" },
  { path: "/insights", icon: BarChart3, tKey: "nav.insights" },
  { path: "/share-portal", icon: Share2, tKey: "nav.share" },
  { path: "/guide", icon: BookOpen, tKey: "nav.guide" },
  { path: "/pricing", icon: CreditCard, tKey: "nav.plans" },
  { path: "/gift", icon: Gift, tKey: "nav.gift" },
  { path: "/settings", icon: Settings, tKey: "nav.settings" },
];

const ADMIN_TIERS = new Set(["team_dev", "diamond"]);

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useI18n();
  const name = user?.persona?.preferred_name || user?.username || "Friend";
  const isAdmin = ADMIN_TIERS.has(user?.tier);
  const [psychLive, setPsychLive] = useState(false);
  const [renewDays, setRenewDays] = useState(null);
  const [renewDismissed, setRenewDismissed] = useState(sessionStorage.getItem("omni_renew_dismissed") === "1");
  useEffect(() => {
    fetch(`${API}/features`).then(r => r.json()).then(d => setPsychLive(d.psychologist_enabled === true)).catch(() => {});
    const tok = localStorage.getItem("omni_token");
    if (!tok) return;
    fetch(`${API}/subscription/status`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.json())
      .then(d => {
        const sub = d.subscription;
        if (!sub || sub.status !== "active" || !sub.expires_at) return;
        const days = Math.ceil((new Date(sub.expires_at) - Date.now()) / 86400000);
        if (days >= 0 && days <= 7) setRenewDays(days);
      })
      .catch(() => {});
  }, []);

  // Build nav items: add Family for premium tiers, add Dashboard for admin tiers
  let navItems = BASE_NAV.filter(i => i.path !== "/therapy" || psychLive);
  if (["duo", "family", "platinum", "diamond", "team_dev"].includes(user?.tier)) {
    const shareIdx = navItems.findIndex(i => i.path === "/share-portal");
    navItems.splice(shareIdx, 0, { path: "/family", icon: Users, tKey: "nav.family" });
  }
  if (["diamond", "team_dev"].includes(user?.tier)) {
    const familyIdx = navItems.findIndex(i => i.path === "/family");
    const insertIdx = familyIdx >= 0 ? familyIdx + 1 : navItems.findIndex(i => i.path === "/share-portal");
    navItems.splice(insertIdx, 0, { path: "/group-admin", icon: Crown, tKey: "nav.group" });
  }
  if (isAdmin) {
    const settingsIdx = navItems.findIndex(i => i.path === "/settings");
    navItems.splice(settingsIdx, 0, { path: "/admin", icon: BarChart3, tKey: "nav.admin" });
  }

  return (
    <div className="h-screen max-w-[100vw] bg-background flex overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden md:flex flex-col border-r border-border/50 bg-card/50 backdrop-blur-sm transition-all duration-300 flex-shrink-0",
        collapsed ? "w-16 lg:w-20" : "w-48 lg:w-64"
      )}>
        <div className="p-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
          {!collapsed && <span className="font-heading text-xl font-medium">Omni</span>}
        </div>

        <nav className="flex-1 px-4 space-y-1">
          {navItems.map(({ path, icon: Icon, tKey }) => {
            const label = t(tKey);
            const active = location.pathname.startsWith(path);
            return (
              <Tip key={path} content={collapsed ? label : null} side="right">
                <Link
                  to={path}
                  data-testid={`nav-${tKey.split('.')[1]}`}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200",
                    active
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  )}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span>{label}</span>}
                </Link>
              </Tip>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border/50">
          {!collapsed && (
            <div className="px-4 py-3 mb-2">
              <p className="font-semibold text-sm sidebar-text">{name}</p>
              <p className="text-xs text-muted-foreground capitalize sidebar-text">{user?.tier || "Free"} plan</p>
            </div>
          )}
          {/* Language Switcher - Prominent */}
          <div className={cn("mb-2 rounded-xl", collapsed ? "px-1" : "px-2 py-2 bg-primary/5 border border-primary/10")}>
            {!collapsed && (
              <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1.5 px-2">Language</p>
            )}
            <LanguageSwitcher collapsed={collapsed} />
          </div>
          <Tip content="Sign out" side="right">
            <Button
              variant="ghost"
              data-testid="logout-btn"
              onClick={logout}
              className="w-full justify-start gap-3 px-4 py-3 rounded-2xl text-muted-foreground hover:text-destructive"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
              {!collapsed && t("nav.logout")}
            </Button>
          </Tip>
          <Tip content={collapsed ? "Expand sidebar" : "Collapse sidebar"} side="right">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="w-full flex items-center justify-center py-2 text-muted-foreground hover:text-foreground mt-2"
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </Tip>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full min-w-0 overflow-hidden pb-20 md:pb-0">
        {renewDays !== null && !renewDismissed && user?.tier !== "team_dev" && (
          <div data-testid="renewal-banner" className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200/60 dark:border-amber-800/40">
            <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" strokeWidth={1.5} />
            <p className="text-xs sm:text-sm text-amber-800 dark:text-amber-300 flex-1 min-w-0 truncate">
              {renewDays === 0 ? "Your plan expires today" : `Your plan expires in ${renewDays} day${renewDays === 1 ? "" : "s"}`} — renew to keep your premium features without interruption.
            </p>
            <Link to="/pricing" data-testid="renewal-banner-renew-btn" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-600 text-white hover:bg-amber-700 transition-colors flex-shrink-0">
              Renew now
            </Link>
            <button data-testid="renewal-banner-dismiss" onClick={() => { setRenewDismissed(true); sessionStorage.setItem("omni_renew_dismissed", "1"); }} className="text-amber-600 dark:text-amber-400 hover:opacity-70 flex-shrink-0" aria-label="Dismiss">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        )}
        {children}
      </main>

      {/* Help Center Floating Button */}
      <HelpCenter />

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-card/80 backdrop-blur-xl border-t border-border/50 z-50 safe-area-bottom" aria-label="Mobile navigation">
        <div className="flex items-center justify-around py-2 px-2">
          {navItems.slice(0, 5).map(({ path, icon: Icon, tKey }) => {
            const label = t(tKey);
            const active = location.pathname.startsWith(path);
            return (
              <Link
                key={path}
                to={path}
                data-testid={`mobile-nav-${tKey.split('.')[1]}`}
                className={cn(
                  "flex flex-col items-center gap-0.5 py-1.5 px-2 min-w-[48px] rounded-xl transition-all touch-manipulation",
                  active ? "text-primary" : "text-muted-foreground"
                )}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-[9px] font-medium leading-tight">{label}</span>
              </Link>
            );
          })}
          <Link
            to="/settings"
            data-testid="mobile-nav-more"
            className={cn(
              "flex flex-col items-center gap-0.5 py-1.5 px-2 min-w-[48px] rounded-xl transition-all touch-manipulation",
              location.pathname === "/settings" ? "text-primary" : "text-muted-foreground"
            )}
          >
            <Settings className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[9px] font-medium leading-tight">More</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};
